/**
 * RTDB-Enhanced NFL Service
 * Wraps NFLService with Firebase Realtime Database caching
 * Maintains exact API compatibility while adding caching layer
 */

import baseNFLService from '../../../../components/dashboards/services/nflService';
import nflRTDBCache from './rtdbCache';
import {
  CACHE_PATHS,
  getCacheKey,
  TTL_CONFIG,
  logCache
} from './rtdbConfig';

const normalizeGamesResponse = (data) => {
  if (!data) return data;
  if (Array.isArray(data)) {
    return {
      get: "games",
      results: data.length,
      response: data,
      cached: true
    };
  }
  return data;
};

class RTDBNFLService {
  constructor() {
    // Use the imported instance directly (it's already instantiated)
    this.baseService = baseNFLService;
    
    // Proxy all direct property access to base service
    this.baseURL = this.baseService.baseURL;
    this.apiKey = this.baseService.apiKey;
    this.debug = this.baseService.debug;
  }

  /**
   * Enhanced getGames with RTDB caching
   * This is the core method that all other game methods use
   */
  async getGames(id = null, date = null, league = null, season = null, team = null, h2h = null, live = null, timezone = null) {
    // Build cache key based on parameters
    let cachePath = null;
    let ttl = TTL_CONFIG.DEFAULT;
    
    if (live === 'all') {
      // Live games - very short cache
      cachePath = `${CACHE_PATHS.GAMES_LIVE}/all`;
      ttl = TTL_CONFIG.LIVE_GAMES;
    } else if (date && !team && !h2h) {
      // Date-based query without team filter
      const cacheKey = `all_${season || 'current'}`;
      cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/${cacheKey}`;
      ttl = TTL_CONFIG.DATE_GAMES;
    } else if (date && team) {
      // Date-based query with team filter
      const cacheKey = `team_${team}_${season || 'current'}`;
      cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/${cacheKey}`;
      ttl = TTL_CONFIG.DATE_GAMES;
    } else if (id) {
      // Single game query - cache individually
      cachePath = `${CACHE_PATHS.GAMES}/single/${id}`;
      ttl = TTL_CONFIG.DEFAULT;
    }
    
    // Try to get from cache if we have a cache path
    if (cachePath) {
      try {
        const cachedData = await nflRTDBCache.get(cachePath);
        if (cachedData) {
          logCache('hit', `Games from cache: ${cachePath}`);
          return normalizeGamesResponse(cachedData);
        }
      } catch (error) {
        logCache('error', 'Cache read failed, falling back to API', error);
      }
    }
    
    // Not in cache or no cache path, fetch from API
    logCache('miss', 'Fetching games from API', { id, date, league, season, team, h2h, live });
    
    try {
      const response = await this.baseService.getGames(id, date, league, season, team, h2h, live, timezone);
      
      // Cache the response if we have a cache path
      if (cachePath && response) {
        // Use smart TTL for game collections
        if (response.response && Array.isArray(response.response)) {
          await nflRTDBCache.setGames(cachePath, response.response, ttl);
        } else {
          await nflRTDBCache.set(cachePath, response, ttl);
        }
      }
      
      return response;
    } catch (error) {
      logCache('error', 'API call failed', error);
      throw error;
    }
  }

  /**
   * Enhanced getGamesForWeek with RTDB caching
   * This is heavily used in mobile dashboard
   */
  async getGamesForWeek(weekId, seasonYear = 2025, league = 1, team = null, timezone = 'America/New_York') {
    // Build cache key for week
    let cachePath;
    let ttl = TTL_CONFIG.WEEK_GAMES;
    
    if (team) {
      cachePath = `${CACHE_PATHS.GAMES_WEEKS}/${weekId}_${seasonYear}_team_${team}`;
    } else {
      cachePath = `${CACHE_PATHS.GAMES_WEEKS}/${weekId}_${seasonYear}`;
    }
    
    // Try cache first
    try {
      const cachedData = await nflRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Week games from cache: ${weekId}, season: ${seasonYear}`);
        return normalizeGamesResponse(cachedData);
      }
    } catch (error) {
      logCache('error', 'Cache read failed for week games', error);
    }
    
    // Fetch from API
    logCache('miss', `Fetching week games from API: ${weekId}, season: ${seasonYear}`);
    
    try {
      const response = await this.baseService.getGamesForWeek(weekId, seasonYear, league, team, timezone);
      
      // Cache the response
      if (response && response.response) {
        // Wrap in standard API response format if needed
        const cacheData = response.response ? response : { response: response };
        await nflRTDBCache.setGames(cachePath, cacheData.response, ttl);
        await nflRTDBCache.set(cachePath, cacheData, ttl);
      }
      
      return response;
    } catch (error) {
      logCache('error', 'API call failed for week games', error);
      throw error;
    }
  }

  /**
   * Enhanced getGamesForSeasons with intelligent caching
   * This fetches multiple seasons but we cache per season
   */
  async getGamesForSeasons(date = null, seasons = [2025, 2024, 2023], league = 1, team = null, timezone = 'America/New_York') {
    const allGames = [];
    const errors = [];
    
    // Fetch each season separately to leverage caching
    const promises = seasons.map(async (season) => {
      try {
        // Use the enhanced getGames method which has caching
        const response = await this.getGames(null, date, league, season, team, null, null, timezone);
        return response.response || [];
      } catch (err) {
        console.warn(`Failed to fetch games for season ${season}:`, err);
        errors.push({ season, error: err.message });
        return [];
      }
    });
    
    const results = await Promise.all(promises);
    
    // Combine all games and sort by date/time
    results.forEach(games => {
      allGames.push(...games);
    });
    
    // Sort games by timestamp
    allGames.sort((a, b) => {
      const timestampA = a.game?.date?.timestamp || 0;
      const timestampB = b.game?.date?.timestamp || 0;
      return timestampA - timestampB;
    });
    
    return {
      get: 'games',
      parameters: { date, seasons, league, team },
      errors: errors.length > 0 ? errors : null,
      results: allGames.length,
      response: allGames
    };
  }

  /**
   * Get standings with caching
   */
  async getStandings(league = null, season = null, team = null, group = null, preseason = null) {
    // Build cache key
    const cacheKey = getCacheKey('standings', league || 1, season || 2025, group || 'all');
    const cachePath = `${CACHE_PATHS.STANDINGS}/${cacheKey}`;
    const ttl = TTL_CONFIG.STANDINGS;
    
    // Try cache first
    try {
      const cachedData = await nflRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Standings from cache: league ${league}, season ${season}`);
        return cachedData;
      }
    } catch (error) {
      logCache('error', 'Cache read failed for standings', error);
    }
    
    // Fetch from API
    logCache('miss', 'Fetching standings from API');
    
    try {
      const response = await this.baseService.getStandings(league, season, team, group, preseason);
      
      // Cache the response
      if (response) {
        await nflRTDBCache.set(cachePath, response, ttl);
      }
      
      return response;
    } catch (error) {
      logCache('error', 'API call failed for standings', error);
      throw error;
    }
  }

  /**
   * Get players with caching
   */
  async getPlayers(id = null, name = null, team = null, season = null, type = null) {
    if (id) {
      // Single player - don't cache
      return await this.baseService.getPlayers(id, name, team, season, type);
    }
    
    // Team players - cache
    const cacheKey = getCacheKey('players', team || 'all', season || 2025);
    const cachePath = `${CACHE_PATHS.PLAYERS}/${cacheKey}`;
    const ttl = TTL_CONFIG.PLAYERS;
    
    // Try cache first
    try {
      const cachedData = await nflRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Players from cache: team ${team}, season ${season}`);
        return cachedData;
      }
    } catch (error) {
      logCache('error', 'Cache read failed for players', error);
    }
    
    // Fetch from API
    logCache('miss', 'Fetching players from API');
    
    try {
      const response = await this.baseService.getPlayers(id, name, team, season, type);
      
      // Cache the response
      if (response && team) {
        await nflRTDBCache.set(cachePath, response, ttl);
      }
      
      return response;
    } catch (error) {
      logCache('error', 'API call failed for players', error);
      throw error;
    }
  }

  /**
   * Get teams with caching
   */
  async getTeams(id = null, league = null, season = null, name = null, code = null) {
    if (id || name || code) {
      // Specific team query - don't cache
      return await this.baseService.getTeams(id, league, season, name, code);
    }
    
    // League teams - cache
    const cacheKey = getCacheKey('teams', league || 1, season || 2025);
    const cachePath = `${CACHE_PATHS.TEAMS}/${cacheKey}`;
    const ttl = TTL_CONFIG.TEAMS;
    
    // Try cache first
    try {
      const cachedData = await nflRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Teams from cache: league ${league}, season ${season}`);
        return cachedData;
      }
    } catch (error) {
      logCache('error', 'Cache read failed for teams', error);
    }
    
    // Fetch from API
    logCache('miss', 'Fetching teams from API');
    
    try {
      const response = await this.baseService.getTeams(id, league, season, name, code);
      
      // Cache the response
      if (response) {
        await nflRTDBCache.set(cachePath, response, ttl);
      }
      
      return response;
    } catch (error) {
      logCache('error', 'API call failed for teams', error);
      throw error;
    }
  }

  // Pass through methods that don't need caching
  async getTodayGames(league = null, timezone = null) {
    const today = new Date().toISOString().split('T')[0];
    return await this.getGames(null, today, league, null, null, null, null, timezone);
  }

  async getLiveGames() {
    return await this.getGames(null, null, null, null, null, null, 'all');
  }

  // Utility methods - pass through to base service
  getTodayDate() {
    return this.baseService.getTodayDate();
  }

  getDefaultGameDate() {
    return this.baseService.getDefaultGameDate();
  }

  getCurrentSeason() {
    return this.baseService.getCurrentSeason();
  }

  getAvailableSeasons() {
    return this.baseService.getAvailableSeasons();
  }

  getWeekDateRange(weekId, seasonYear) {
    return this.baseService.getWeekDateRange(weekId, seasonYear);
  }

  formatTime(datetime) {
    return this.baseService.formatTime(datetime);
  }

  getFullDateTime(datetime) {
    return this.baseService.getFullDateTime(datetime);
  }

  getConferenceName(conference) {
    return this.baseService.getConferenceName(conference);
  }

  // Cache management methods
  clearCache() {
    // Clear both in-memory and RTDB cache
    this.baseService.clearCache();
    nflRTDBCache.clear(CACHE_PATHS.ROOT);
    logCache('info', 'All NFL caches cleared');
  }

  async getCacheStats() {
    return await nflRTDBCache.getStats();
  }

  // Pass through other methods
  async getConferences(league, season) {
    return await this.baseService.getConferences(league, season);
  }

  async getDivisions(league, season) {
    return await this.baseService.getDivisions(league, season);
  }

  async getGameEvents(gameId) {
    return await this.baseService.getGameEvents(gameId);
  }

  async getGameTeamStatistics(gameId, team = null) {
    return await this.baseService.getGameTeamStatistics(gameId, team);
  }

  async getGamePlayerStatistics(gameId, team = null) {
    return await this.baseService.getGamePlayerStatistics(gameId, team);
  }

  // Odds endpoints (pass through - not cached due to frequent changes)
  async getOdds(game, bookmaker = null, bet = null) {
    return await this.baseService.getOdds(game, bookmaker, bet);
  }

  async getBets(id = null, search = null) {
    return await this.baseService.getBets(id, search);
  }

  async getBookmakers(id = null, search = null) {
    return await this.baseService.getBookmakers(id, search);
  }

  // Manual API key setting (pass through)
  setApiKey(key) {
    this.baseService.setApiKey(key);
    this.apiKey = key;
  }
}

// Create singleton instance
const rtdbNFLService = new RTDBNFLService();

// Export as default to match NFLService usage
export default rtdbNFLService;

// Also export as named for explicit usage
export { rtdbNFLService };

// Browser debugging helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.NFLRTDBService = {
    instance: rtdbNFLService,
    getCacheStats: () => rtdbNFLService.getCacheStats(),
    clearCache: () => rtdbNFLService.clearCache()
  };
}
